import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowRight, Smartphone, Loader2 } from 'lucide-react';
import AdPlanPicker from '../components/AdPlanPicker';
import PhoneNumberLockup from '../components/PhoneNumberLockup';
import { handleAdSaved, adErrorMessage, MY_ADS_URL } from '../utils/adSubmit';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import axios from 'axios';
import { toast } from 'sonner';
import { useLanguage } from '../config/language';
import { useTheme } from '../config/theme';
import { usePhoneOperators } from '../hooks/usePhoneOperators';
import { operatorLogo, operatorSlug, operatorBrand } from '../constants/operators';
import OperatorLogo from '../components/OperatorLogo';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const DOT_COLORS = {
  'amber-500': 'bg-amber-500',
  'yellow-500': 'bg-amber-500',
  'gray-400': 'bg-gray-400',
  'blue-500': 'bg-blue-500',
};

export default function AddPhoneListing() {
  const { language } = useLanguage();
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();
  const { id: editId } = useParams();
  const isEdit = Boolean(editId);
  const isAr = language === 'ar';

  const [schema, setSchema] = useState(null);
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const { operators, error: opsError } = usePhoneOperators();
  const [operatorId, setOperatorId] = useState('');
  const [savedOp, setSavedOp] = useState(null);
  const [prefix, setPrefix] = useState('');
  const [number, setNumber] = useState('');
  const [category, setCategory] = useState('normal');
  const [paymentType, setPaymentType] = useState('prepaid');
  const [negotiable, setNegotiable] = useState(false);
  const [hasContract, setHasContract] = useState(false);
  const [price, setPrice] = useState('');
  const [description, setDescription] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/listing-schemas`);
        const s = res.data?.schemas?.phone_numbers;
        setSchema(s);
        const defCat = s?.categories?.find((c) => c.default);
        if (defCat) setCategory(defCat.value);
        const defPay = s?.payment_types?.find((p) => p.default);
        if (defPay) setPaymentType(defPay.value);
        if (editId) {
          const adRes = await axios.get(`${BACKEND_URL}/api/user/ads/${editId}`, { headers: getAuthHeaders() });
          const ad = adRes.data || {};
          const d = ad.details || {};
          setSavedOp({ id: d.operator_id, slug: d.operator, name_ar: d.operator_name_ar, name_en: d.operator_name_en, codes: d.prefix ? [d.prefix] : [] });
          if (d.operator_id) setOperatorId(d.operator_id);
          if (d.prefix) setPrefix(d.prefix);
          if (d.number) setNumber(d.number);
          if (d.number_category) setCategory(d.number_category);
          if (d.paymentType) setPaymentType(d.paymentType);
          setNegotiable(Boolean(d.negotiable ?? ad.is_negotiable));
          setHasContract(Boolean(d.hasContract));
          setPrice(ad.price != null ? String(ad.price) : '');
          setDescription(ad.description || '');
        }
      } catch {
        toast.error(isAr ? 'تعذر تحميل نموذج الإعلان' : 'Failed to load listing form');
      } finally {
        setLoading(false);
      }
    })();
  }, [isAr, editId]);

  // Operator disabled by the admin since the ad was saved → still shown (read-only) so the edit form keeps its value
  const allOps = useMemo(
    () => (savedOp?.id && !operators.some((o) => o.id === savedOp.id) ? [...operators, { ...savedOp, disabled: true }] : operators),
    [operators, savedOp]
  );
  const currentOperator = allOps.find((o) => o.id === operatorId);

  useEffect(() => {
    if (loading || !operators.length || operatorId) return;
    const legacy = savedOp?.slug && operators.find((o) => o.slug === operatorSlug(savedOp.slug));
    const op = legacy || operators[0];
    setOperatorId(op.id);
    setPrefix((p) => (legacy && op.codes.includes(p) ? p : op.codes[0] || ''));
  }, [loading, operators, operatorId, savedOp]);

  const handleOperatorChange = (id) => {
    setOperatorId(id);
    const op = allOps.find((o) => o.id === id);
    if (op && !op.codes.includes(prefix)) setPrefix(op.codes[0] || '');
  };

  const handleNumberChange = (val) => {
    setNumber(val.replace(/\D/g, '').slice(0, schema?.number_input?.number_digits || 7));
  };

  const handleSubmit = async () => {
    const digits = schema?.number_input?.number_digits || 7;
    if (!currentOperator || !prefix) {
      toast.error(isAr ? 'يرجى اختيار المشغّل والكود' : 'Please choose the operator and code');
      return;
    }
    if (number.length !== digits) {
      toast.error(schema?.validation_messages?.number?.[isAr ? 'ar' : 'en'] || (isAr ? `يرجى إدخال رقم صحيح (${digits} أرقام)` : `Enter a valid ${digits}-digit number`));
      return;
    }
    if (!price || parseFloat(price) <= 0) {
      toast.error(schema?.validation_messages?.price?.[isAr ? 'ar' : 'en'] || (isAr ? 'يرجى إدخال سعر صحيح' : 'Enter a valid price'));
      return;
    }
    if (!isEdit && plan && !plan.ready) {
      toast.error(isAr ? 'يرجى اختيار مدة الإعلان' : 'Please choose the ad duration');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        operator_id: operatorId, operator: currentOperator.slug, prefix, number, category, paymentType,
        negotiable, hasContract,
        price: parseFloat(price), description,
      };
      if (isEdit) {
        const res = await axios.put(`${BACKEND_URL}/api/user/ads/phones/${editId}`, payload, { headers: getAuthHeaders() });
        handleAdSaved(res.data, { navigate, isAr, title: prefix + number });
      } else {
        if (plan && !plan.isFree) payload.tier_id = plan.tierId;
        const res = await axios.post(`${BACKEND_URL}/api/user/ads/phone`, payload, { headers: getAuthHeaders() });
        handleAdSaved(res.data, { navigate, isAr, title: prefix + number });
      }
    } catch (err) {
      toast.error(adErrorMessage(err, isAr));
    } finally {
      setSubmitting(false);
    }
  };

  const cardCls = isDarkMode ? 'bg-[#141414] border border-white/10' : 'bg-white border border-[#E2E8F0]';
  const chipBase = 'rounded-xl border-2 px-4 py-3 cursor-pointer transition-colors flex items-center gap-2';
  const chipInactive = isDarkMode ? 'border-white/10 hover:border-white/30' : 'border-[#E2E8F0] hover:border-[#94A3B8]';
  const chipActive = 'border-[#1E9BD9] bg-[#1E9BD9]/10';

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[#1E9BD9]" />
      </div>
    );
  }

  return (
    <div className={`min-h-screen py-8 px-4 ${isDarkMode ? 'bg-[#0a0a0a] text-white' : 'bg-[#F8FAFC] text-[#0F172A]'}`} data-testid="add-phone-listing-page">
      <div className="max-w-3xl mx-auto">
        <button
          onClick={() => navigate(MY_ADS_URL)}
          className="flex items-center gap-2 text-[#1E9BD9] mb-6 hover:underline"
          data-testid="back-to-dashboard-btn"
        >
          <ArrowRight className={`h-4 w-4 ${!isAr ? 'rotate-180' : ''}`} />
          {isAr ? 'العودة إلى إعلاناتي' : 'Back to My Ads'}
        </button>

        <h1 className="text-2xl font-bold mb-6 flex items-center gap-2">
          <Smartphone className="h-6 w-6 text-[#1E9BD9]" />
          {isEdit ? (isAr ? 'تعديل إعلان رقم هاتف' : 'Edit Phone Number Ad') : (isAr ? 'إضافة إعلان رقم هاتف' : 'Add Phone Number Ad')}
        </h1>

        {!isEdit && (
          <div className="mb-6">
            <AdPlanPicker category="phones" isAr={isAr} isDarkMode={isDarkMode} value={plan} onChange={setPlan} />
          </div>
        )}

        {/* Preview Card */}
        <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-2xl p-6 mb-6 flex items-center justify-between" data-testid="phone-preview-card">
          <div className="flex items-center gap-4">
            {currentOperator && (
              <span className="w-12 h-12 rounded-lg bg-white p-1 inline-flex items-center justify-center shrink-0">
                <OperatorLogo
                  src={operatorLogo(currentOperator)}
                  name={currentOperator[isAr ? 'name_ar' : 'name_en'] || currentOperator.name_en}
                  accent={operatorBrand(currentOperator.slug).accent}
                  className="w-10 h-10"
                  letterClass="text-lg"
                  testId="phone-preview-logo"
                />
              </span>
            )}
            <div>
              <p className="text-white/60 text-sm" data-testid="phone-preview-operator">{currentOperator?.[isAr ? 'name_ar' : 'name_en']}</p>
              <PhoneNumberLockup
                value={`${prefix}${number || (schema?.preview?.empty_placeholder || '0000000')}`}
                size="md"
                tone="light"
                testId="phone-preview-number"
              />
            </div>
          </div>
          {price && <p className="text-[#F5A962] text-xl font-bold" dir="ltr">{parseFloat(price).toLocaleString()} AED</p>}
        </div>

        <div className={`rounded-2xl p-6 space-y-6 ${cardCls}`}>
          {/* Operator (list managed by the admin platform) */}
          <div>
            <Label className="mb-2 block">{isAr ? 'المشغل' : 'Operator'} *</Label>
            {opsError && !allOps.length ? (
              <p className="text-sm text-red-500" data-testid="phone-operators-error">
                {isAr ? 'تعذّر تحميل قائمة المشغّلين، حاول بعد قليل' : 'Could not load the operators list, please try again shortly'}
              </p>
            ) : (
              <Select value={operatorId} onValueChange={handleOperatorChange}>
                <SelectTrigger className={`h-12 ${isDarkMode ? 'bg-white/10 border-white/20' : ''}`} data-testid="phone-operator-select">
                  <SelectValue placeholder={isAr ? 'اختر المشغّل' : 'Choose operator'} />
                </SelectTrigger>
                <SelectContent className={isDarkMode ? 'bg-[#141414] text-white border-white/10' : ''}>
                  {allOps.map((op) => (
                    <SelectItem key={op.id} value={op.id} disabled={op.disabled} data-testid={`operator-option-${op.slug || op.id}`}>
                      <span className="flex items-center gap-2">
                        <OperatorLogo src={operatorLogo(op)} name={op[isAr ? 'name_ar' : 'name_en'] || op.name_en} accent={operatorBrand(op.slug).accent} className="w-6 h-6" letterClass="text-xs" />
                        <span className="font-bold">{op[isAr ? 'name_ar' : 'name_en'] || op.name_en}</span>
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          {/* Code + Number */}
          <div>
            <Label className="mb-2 block">{isAr ? 'الرقم' : 'Number'} *</Label>
            <div className="flex gap-2" dir="ltr">
              <select
                value={prefix}
                onChange={(e) => setPrefix(e.target.value)}
                disabled={!currentOperator}
                className={`rounded-md border px-3 py-0 h-10 leading-none text-sm font-bold ${isDarkMode ? 'bg-white/10 border-white/20 text-white' : 'bg-white border-[#E2E8F0]'}`}
                data-testid="phone-prefix-select"
              >
                {currentOperator?.codes?.map((p) => (
                  <option key={p} value={p}>{p}</option>
                ))}
              </select>
              <Input
                value={number}
                onChange={(e) => handleNumberChange(e.target.value)}
                placeholder="1234567"
                className="tracking-widest font-bold"
                dir="ltr"
                data-testid="phone-number-input"
              />
            </div>
            <p className="text-xs text-[#94A3B8] mt-1">
              {isAr
                ? (schema?.number_input?.counter_label_ar || 'أدخل 7 أرقام ({n}/7)').replace('{n}', number.length)
                : `Enter ${schema?.number_input?.number_digits || 7} digits (${number.length}/${schema?.number_input?.number_digits || 7})`}
            </p>
          </div>

          {/* Category */}
          <div>
            <Label className="mb-2 block">{isAr ? 'الفئة' : 'Category'}</Label>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {schema?.categories?.map((c) => (
                <div
                  key={c.value}
                  className={`${chipBase} justify-center ${category === c.value ? chipActive : chipInactive}`}
                  onClick={() => setCategory(c.value)}
                  data-testid={`category-${c.value}`}
                >
                  <span className={`w-2.5 h-2.5 rounded-full ${DOT_COLORS[c.dot_color] || 'bg-blue-500'}`} />
                  <span className="text-sm font-medium">{c[isAr ? 'label_ar' : 'label_en']}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Payment Type */}
          <div>
            <Label className="mb-2 block">{isAr ? 'نوع الدفع' : 'Payment Type'}</Label>
            <div className="grid grid-cols-2 gap-3">
              {schema?.payment_types?.map((p) => (
                <div
                  key={p.value}
                  className={`${chipBase} justify-center ${paymentType === p.value ? chipActive : chipInactive}`}
                  onClick={() => setPaymentType(p.value)}
                  data-testid={`payment-${p.value}`}
                >
                  <span className="text-sm font-medium">{p[isAr ? 'label_ar' : 'label_en']}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Toggles */}
          <div className="grid sm:grid-cols-2 gap-4">
            {schema?.toggles?.map((t) => {
              const value = t.name === 'negotiable' ? negotiable : hasContract;
              const setter = t.name === 'negotiable' ? setNegotiable : setHasContract;
              return (
                <div key={t.name} className={`flex items-center justify-between rounded-xl border p-4 ${isDarkMode ? 'border-white/10' : 'border-[#E2E8F0]'}`}>
                  <span className="font-medium">{t[isAr ? 'label_ar' : 'label_en']}</span>
                  <Switch checked={value} onCheckedChange={setter} data-testid={`toggle-${t.name}`} />
                </div>
              );
            })}
          </div>

          {/* Price + Description (from schema fields) */}
          {schema?.fields?.map((f) => (
            <div key={f.name}>
              <Label className="mb-2 block">
                {f[isAr ? 'label_ar' : 'label_en']} {f.required && '*'}
              </Label>
              {f.type === 'number' ? (
                <div className="relative">
                  <Input
                    type="number"
                    value={price}
                    onChange={(e) => setPrice(e.target.value)}
                    placeholder="0"
                    min={f.min}
                    data-testid={`field-${f.name}`}
                  />
                  {f.suffix && <span className="absolute end-3 top-1/2 -translate-y-1/2 text-sm text-[#94A3B8]">{f.suffix}</span>}
                </div>
              ) : (
                <Textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={f.rows || 3}
                  data-testid={`field-${f.name}`}
                />
              )}
            </div>
          ))}

          <Button
            onClick={handleSubmit}
            disabled={submitting || !currentOperator}
            className="w-full bg-[#1E9BD9] hover:bg-[#1786be] text-white h-11 text-base"
            data-testid="submit-phone-ad-btn"
          >
            {submitting ? <Loader2 className="h-5 w-5 animate-spin" /> : isEdit ? (isAr ? 'حفظ التعديلات' : 'Save Changes') : plan && !plan.isFree && plan.ready ? (isAr ? `نشر والدفع (${plan.fee} ${plan.currency})` : `Publish & Pay (${plan.fee} ${plan.currency})`) : (isAr ? 'نشر الإعلان' : 'Publish Ad')}
          </Button>
        </div>
      </div>
    </div>
  );
}
